function isArray(obj) {
    return !!obj && obj.constructor === Array;
}

function isObject(obj) {
    return typeof obj === 'object' && obj !== null
}

/* ── COMPARISON RESPONSES (PRM-518) ────────────────────────────────────────────
   An API that supports two date ranges answers with two arrays rather than one:

     { Status: true, Result: [...period 1...], Result2: [...period 2...] }

   Concatenated, those become one list in which the same category appears twice with
   nothing saying which window each row belongs to - a comparison report that cannot
   be read, and worse, one whose numbers look plausible. So a multi-array response is
   laid out SIDE BY SIDE instead: one row carrying both periods' fields, each namespaced
   with the response key it came from.

     { Result__TotalSales: 63.88, Result2__TotalSales: 38.93 }

   The namespace is the API's own key, not a position, so a column says where it came
   from. The widget strips it for display and groups the columns under the period's
   name - see PERIOD_SEPARATOR's consumers.

   ── ALIGNED BY POSITION, NOT BY KEY ──
   Row 1 of Result sits beside row 1 of Result2. There is deliberately no join column:
   one would have to be chosen per report and choosing wrong is silent. The cost is
   that if an API ever returns a category in one period and not the other, everything
   below it shifts and the comparison is wrong with nothing to show for it. Watch for
   it; adding a join later does not change the column structure below. */

export const PERIOD_SEPARATOR = "__";

/** Split "Result2__TotalSales" into its response key and field name. */
export function splitPeriodField(name) {
    const at = String(name || "").indexOf(PERIOD_SEPARATOR);
    if (at === -1) return { periodKey: null, fieldName: name };
    return {
        periodKey: String(name).slice(0, at),
        fieldName: String(name).slice(at + PERIOD_SEPARATOR.length),
    };
}

/* One row per position, holding every array's fields.

   ── THE FIRST ARRAY KEEPS ITS BARE NAMES ──
   Only the SECOND and later arrays are namespaced. Whether a comparison response
   comes back at all depends on what the reader picked - ask for one date range and
   the API answers with Result alone, add a second and it answers with Result2 too -
   so namespacing everything made a report's column names change the moment a filter
   was applied. The board's saved columns then matched nothing and the table rendered
   blank. Leaving the first array alone keeps period 1 identical in both cases, and
   period 2's columns are purely additive.

   Field names come from the WHOLE array rather than its first row: a response whose
   first entry happens to omit a field would otherwise lose that column for every row
   below it. */
function buildPeriodRows(resObj, keys, matchBy) {
    const fieldsByKey = {};
    let longest = 0;

    keys.forEach((key) => {
        const rows = resObj[key] || [];
        longest = Math.max(longest, rows.length);

        const fields = [];
        rows.forEach((row) => {
            if (!row || typeof row !== "object") return;
            Object.keys(row).forEach((f) => {
                if (fields.indexOf(f) === -1) fields.push(f);
            });
        });
        fieldsByKey[key] = fields;
    });

    /* ── MATCHED BY A KEY COLUMN (an OUTER join) ──
       With a key, a row is identified by its VALUE rather than by its position, which
       is the only way the comparison is trustworthy: the API is under no obligation to
       return both periods in the same order, and ours does not - one response came back
       MAINS/STARTER/SIDE and the other SIDE/MAINS/STARTER, so aligning by position was
       already pairing a category against a different one.

       OUTER, not inner: every key seen in ANY period gets a row. A category that sold
       in one window and not the other is exactly what a comparison is for, and dropping
       it (which is what matching only on pairs would do) loses a real figure silently.
       The side with no row is left null and the table shows a dash. */
    if (matchBy) {
        const keyOrder = [];
        const byKeyPerPeriod = {};

        keys.forEach((key) => {
            const index = {};
            (resObj[key] || []).forEach((row) => {
                if (!row || typeof row !== "object") return;
                const value = row[matchBy];
                if (value === undefined) return;
                // first row wins a duplicate key - the alternative is silently summing
                if (!(value in index)) index[value] = row;
                if (keyOrder.indexOf(value) === -1) keyOrder.push(value);
            });
            byKeyPerPeriod[key] = index;
        });

        return keyOrder.map((value) => {
            const row = {};
            keys.forEach((key, periodIndex) => {
                const source = byKeyPerPeriod[key][value] || null;
                fieldsByKey[key].forEach((f) => {
                    const column = periodIndex === 0 ? f : `${key}${PERIOD_SEPARATOR}${f}`;
                    row[column] = source ? source[f] : null;
                });
            });
            // the key column itself always has a value, whichever period supplied it
            row[matchBy] = value;
            return row;
        });
    }

    const out = [];
    for (let i = 0; i < longest; i++) {
        const row = {};
        keys.forEach((key, periodIndex) => {
            /* Missing when this period has fewer rows. The keys are still written, as
               null, so the column exists for every row and the table does not go ragged. */
            const source = (resObj[key] || [])[i] || null;
            fieldsByKey[key].forEach((f) => {
                // period 1 keeps its bare names - see the note above
                const column = periodIndex === 0 ? f : `${key}${PERIOD_SEPARATOR}${f}`;
                row[column] = source ? source[f] : null;
            });
        });
        out.push(row);
    }

    return out;
}

export function returnDataArrayObject(resObj, availableFields, options) {
    const matchBy = options && options.matchBy ? options.matchBy : null;
    let data = [];
    if (isObject(resObj)) {

        /* ── ALREADY EXTRACTED ──
           Callers pass this function's own output back into it (BoardChartContainer
           does, on the rows it was handed). Before periodKeys existed that was
           harmless - one array in, the same array out. Now it is not: `data` and
           `periodKeys` are BOTH arrays, so a second pass read its own result as a
           two-period response and namespaced it into nonsense. Recognise it and hand
           it straight back. */
        if (isArray(resObj.data) && isArray(resObj.periodKeys)) return resObj;

        if (isArray(resObj)) {
            // let formattedData  = resObj.map(ro => {})

            return { data: resObj }
        } else {
            const fieldNames = Object.keys(resObj)
            const arrayKeys = fieldNames.filter((nm) => isArray(resObj[nm]));

            /* TWO OR MORE arrays means a comparison response - see the note above.
               One array is every ordinary report there has ever been, and takes the
               original path untouched: this must not change what they return. */
            if (arrayKeys.length > 1) {
                return {
                    data: buildPeriodRows(resObj, arrayKeys, matchBy),
                    // which response keys made the columns, in order, for the headers
                    periodKeys: arrayKeys,
                };
            }

            fieldNames.forEach(nm => {
                if (isArray(resObj[nm])) {
                    data = [...data, ...resObj[nm]];
                    return;
                }
            })

            return (
                { data: data }
            )
        }

    }

    // Always hand back the same shape. Callers read `.data` straight off the result,
    // so returning undefined here (which happened whenever an API replied with text
    // instead of JSON — a proxy error page, an HTML login redirect) threw
    // "Cannot read property 'data' of undefined" and took the whole app down.
    return { data: [] }

}
